/* Ouder-portaal "Meer"-pagina: eenvoudige snelkoppelingen die passen bij de
 * onderste tab-bar. Bewust minimaal — Dagverslag/Berichten/Planning krijgen
 * in Stap 2/3 hun eigen volledige pagina. */
import { useEffect, useState } from "react";
import { Navigate, useNavigate, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Baby, LogOut, MessageSquare, Smartphone, ChevronRight, Mail, Info, Pill } from "lucide-react";
import api from "@/lib/api";
import { isIos, isStandalone } from "@/lib/pwa";
import PushOptInCard from "@/components/PushOptInCard";
import PushPreferences from "@/components/PushPreferences";
import ConsentControls from "@/components/ConsentControls";

function Row({ to, onClick, icon: Icon, label, hint, testid, danger, badge }) {
  const inner = (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${danger ? "bg-red-50 text-red-600" : "bg-[color:var(--sage-100)] text-[color:var(--sage)]"}`}>
        <Icon className="h-4 w-4" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${danger ? "text-red-700" : "text-neutral-800"} leading-tight flex items-center gap-2`}>
          {label}
          {badge > 0 && (
            <span className="min-w-[18px] h-[18px] px-1.5 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold">
              {badge}
            </span>
          )}
        </div>
        {hint && <div className="text-[11px] text-neutral-500 leading-tight mt-0.5">{hint}</div>}
      </div>
      {!danger && <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />}
    </div>
  );
  if (to) return <NavLink to={to} data-testid={testid} className="block hover:bg-neutral-50">{inner}</NavLink>;
  return <button type="button" onClick={onClick} data-testid={testid} className="block w-full text-left hover:bg-neutral-50">{inner}</button>;
}

export default function PortaalMeerPage() {
  const { user, logout, status } = useAuth();
  const navigate = useNavigate();
  const [medPending, setMedPending] = useState(0);

  useEffect(() => {
    if (user?.role !== "ouder") return;
    (async () => {
      try {
        const r = await api.get("/medicatieformulieren?status=wacht_ouder");
        setMedPending((r.data?.items || []).length);
      } catch (e) { console.debug("Kon medicatie-badge niet laden:", e); }
    })();
  }, [user?.role]);

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center bg-[color:var(--bone)]">Laden…</div>;
  if (status === "anon") return <Navigate to="/login" replace />;
  if (user?.role !== "ouder") return <Navigate to="/app" replace />;

  const initials = user?.name?.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase() || "U";
  const installed = isStandalone();

  const onLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[color:var(--bone)] subtle-grain" data-testid="portaal-meer-page">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--sage)" }}>
            <Baby className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-sm leading-tight truncate">Meer</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ouderportaal</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 pb-24 lg:pb-8 space-y-4">
        <Card className="p-4 bg-white shadow-none flex items-center gap-3" data-testid="portaal-meer-profile">
          <Avatar className="h-11 w-11 border border-neutral-200">
            <AvatarFallback className="bg-[color:var(--terracotta)] text-white text-sm font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="text-sm font-medium text-neutral-800 truncate">{user?.name}</div>
            <div className="text-xs text-neutral-500 truncate">{user?.email}</div>
          </div>
        </Card>

        <Card className="bg-white shadow-none overflow-hidden">
          <PushOptInCard />
        </Card>

        <Card className="bg-white shadow-none overflow-hidden" data-testid="portaal-meer-push-prefs">
          <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-500 border-b border-neutral-100">
            Meldingsvoorkeuren
          </div>
          <PushPreferences />
        </Card>

        <Card className="bg-white shadow-none overflow-hidden" data-testid="portaal-meer-consent">
          <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-500 border-b border-neutral-100">
            Toestemmingen
          </div>
          <ConsentControls />
        </Card>

        <Card className="bg-white shadow-none divide-y divide-neutral-100 overflow-hidden">
          <Row
            to="/portaal"
            icon={Baby}
            label="Terug naar Home"
            hint="Overzicht van kinderen en facturen"
            testid="portaal-meer-home"
          />
          <Row
            to="/portaal/medicatie"
            icon={Pill}
            label="Medicatie ondertekenen"
            hint={medPending > 0
              ? `${medPending} formulier${medPending === 1 ? "" : "en"} wacht op jouw toestemming`
              : "Bekijk actieve medicatieformulieren van je kind"}
            testid="portaal-meer-medicatie"
            badge={medPending}
          />
          <Row
            onClick={() => document.location.assign("mailto:info@nannypoppins.nl")}
            icon={Mail}
            label="Neem contact op"
            hint="Stuur ons een e-mail"
            testid="portaal-meer-contact"
          />
          <Row
            to="/portaal"
            icon={MessageSquare}
            label="Berichten van de leidsters"
            hint="Bekijk je schriftje en chat"
            testid="portaal-meer-berichten"
          />
          <Row
            onClick={() => {
              // Re-trigger install banner door dismiss-flag te wissen en reload.
              try { localStorage.removeItem("np_pwa_dismissed_at"); } catch (err) { console.debug("localStorage niet beschikbaar:", err); }
              window.location.reload();
            }}
            icon={Smartphone}
            label={installed ? "App is geïnstalleerd" : "App op je beginscherm zetten"}
            hint={installed ? "Je gebruikt de app-versie" : (isIos() ? "Instructies voor iPhone/iPad" : "Snelle toegang zonder browser")}
            testid="portaal-meer-install"
          />
          <Row
            to="/portaal"
            icon={Info}
            label="Over Webster"
            hint="Kinderopvang OS · v1.0"
            testid="portaal-meer-about"
          />
        </Card>

        <Card className="bg-white shadow-none overflow-hidden">
          <Row
            onClick={onLogout}
            icon={LogOut}
            label="Uitloggen"
            hint={user?.email}
            testid="portaal-meer-logout"
            danger
          />
        </Card>

        <div className="text-center text-[11px] text-neutral-400 pt-2">
          Powered by <span className="font-semibold text-[color:var(--sage)]">Webster</span>
        </div>
      </main>
    </div>
  );
}
